import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Sparkles, BookOpen, Lock, Crown, CheckCircle, Target, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

/**
 * Lessons Hub — pick a topic, view remaining free lessons, start an AI lesson.
 */
const Lessons = () => {
  const { user } = useAuth();
  const { t } = useSettings();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [lessons, setLessons] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);

  const categoryFromUrl = params.get("category");
  const topicFromUrl = params.get("topic");
  const [category, setCategory] = useState(categoryFromUrl || user?.test || "staar");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listData, usageData] = await Promise.all([
          api.listLessons(category),
          api.getLessonsUsage(),
        ]);
        if (cancelled) return;
        setLessons(listData);
        setUsage(usageData);
      } catch (e) {
        toast.error("Could not load lessons");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category]);

  // Auto-start if topic passed in URL
  useEffect(() => {
    if (topicFromUrl && !starting && !loading && usage?.can_start_lesson) {
      setSelectedTopic(topicFromUrl);
    }
  }, [topicFromUrl, loading, usage, starting]);

  const startLesson = async (topic) => {
    if (starting) return;
    if (usage && usage.can_start_lesson === false) {
      toast.error(t("lessons_upgrade"));
      return;
    }
    setStarting(true);
    toast.info("Archie is preparing your lesson...", { duration: 2500 });
    try {
      const lesson = await api.startLesson(category, topic);
      if (lesson && lesson.id) {
        navigate(`/lessons/${lesson.id}`);
      } else {
        toast.error("Could not start lesson");
        setStarting(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "lessons_limit_reached") {
        toast.error(detail.message || t("lessons_upgrade"));
      } else {
        toast.error("Failed to start lesson");
      }
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin">
          <Sparkles className="w-12 h-12 text-indigo-500" />
        </div>
      </div>
    );
  }

  const remaining = usage?.remaining ?? 0;
  const isPremium = !!usage?.is_premium;

  return (
    <div className="space-y-6" data-testid="lessons-hub">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-6 lg:p-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading font-black text-2xl lg:text-3xl mb-2">
              {t("lessons_title")}
            </h1>
            <p className="text-white/80">{t("lessons_subtitle")}</p>
          </div>
          <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full flex items-center gap-2">
            {isPremium ? (
              <>
                <Crown className="w-4 h-4" />
                <span className="font-bold text-sm">{t("lessons_unlimited")}</span>
              </>
            ) : (
              <span className="font-bold text-sm" data-testid="lessons-remaining">
                {remaining} {t("lessons_remaining")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Math Reference quick-link */}
      <Link
        to="/reference/math"
        className="card-pop flex items-center gap-4 hover:border-[#00D46A] transition-colors"
        data-testid="lessons-math-reference-link"
      >
        <div className="w-12 h-12 rounded-xl bg-[#00D46A] flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-6 h-6 text-[#0A1628]" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-white">Math Reference Sheet</p>
          <p className="text-sm text-[#94A3B8]">Algebra · Exponents · Logs · Quadratic · Complex Numbers · Factoring</p>
        </div>
        <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
      </Link>

      {/* Free-tier banner */}
      {!isPremium && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-900 font-medium">
              {t("lessons_free_limit")} — {remaining > 0 ? `${remaining} left` : "limit reached"}
            </p>
          </div>
          <Link
            to="/membership"
            className="text-sm font-bold text-amber-900 underline hover:text-amber-700"
            data-testid="lessons-upgrade-link"
          >
            {t("lessons_upgrade")}
          </Link>
        </div>
      )}

      {/* Recommended topics */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-500" />
          {t("pick_topic")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {(lessons?.recommended_topics || []).map((topic) => {
            const isCompleted = (lessons?.completed || []).some((id) => id.startsWith(topic));
            const active = selectedTopic === topic;
            return (
              <button
                key={topic}
                onClick={() => startLesson(topic)}
                disabled={starting || !usage?.can_start_lesson}
                data-testid={`lesson-topic-${topic}`}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  active
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{topic}</p>
                      <p className="text-xs text-slate-500">AI-generated lesson</p>
                    </div>
                  </div>
                  {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Lessons;
